<template>
  <div
    class="vc-section vc-card vc-card--compact vc-preset-opening"
    :class="{ 'vc-preset-opening--collapsed': !callControl.preset_opening_enabled }"
  >
    <div class="vc-preset-opening__head">
      <div class="vc-preset-opening__title-wrap">
        <span class="vc-preset-opening__title">预设开场白</span>
        <span class="vc-preset-opening__hint">预设开场白不支持打断</span>
      </div>
      <div class="vc-preset-opening__switch">
        <span class="vc-preset-opening__switch-label">{{
          callControl.preset_opening_enabled ? '开启' : '关闭'
        }}</span>
        <i-switch v-model="callControl.preset_opening_enabled" />
      </div>
    </div>

    <div v-show="callControl.preset_opening_enabled" class="vc-preset-opening__body">
      <div class="vc-opening-wrap">

          <div class="vc-opening-editor" :class="{ 'is-focused': openingEditorFocused }">
            <div
              ref="openingHighlight"
              class="vc-opening-highlight"
              aria-hidden="true"
              v-html="openingHighlightHtml"
            />
            <textarea
              ref="openingTextarea"
              v-model="callControl.preset_opening.text"
              class="vc-opening-textarea"
              maxlength="500"
              placeholder="请输入预设开场白"
              @input="syncOpeningHighlight"
              @scroll="syncOpeningHighlightScroll"
              @focus="onOpeningTextareaFocus"
              @blur="onOpeningTextareaBlur"
              @mouseup="saveOpeningCaret"
              @keyup="saveOpeningCaret"
              @select="saveOpeningCaret"
            />
            <div class="vc-opening-toolbar">
              <div
                ref="pauseDropdown"
                class="vc-opening-dropdown"
                :class="{ 'is-open': pausePopoverVisible }"
              >
                <Button
                  class="vc-opening-tool-btn"
                  @mousedown.native.prevent="saveOpeningCaret"
                  @click.stop="togglePausePopover"
                >
                  插入停顿
                </Button>
                <div class="vc-opening-popover" @click.stop>
                  <button
                    v-for="sec in pausePresets"
                    :key="sec"
                    type="button"
                    class="vc-pause-preset"
                    @mousedown.prevent
                    @click="insertPause(sec)"
                  >
                    停顿{{ sec }}秒
                  </button>
                  <div class="vc-pause-custom">
                    <InputNumber
                      v-model="pauseCustomSec"
                      :min="0"
                      :step="0.1"
                      :precision="1"
                      class="vc-pause-custom__input"
                    />
                    <span class="vc-pause-custom__unit">秒</span>
                  </div>
                  <Button type="primary" size="small" long class="vc-pause-confirm" @click="confirmCustomPause">
                    确认
                  </Button>
                </div>
              </div>

              <div
                ref="paramDropdown"
                class="vc-opening-dropdown"
                :class="{ 'is-open': paramPopoverVisible }"
              >
                <Button
                  class="vc-opening-tool-btn"
                  @mousedown.native.prevent="saveOpeningCaret"
                  @click.stop="toggleParamPopover"
                >
                  插入参数
                </Button>
                <div class="vc-opening-popover vc-opening-popover--param" @click.stop>
                  <button
                    v-for="item in paramInsertOptions"
                    :key="item.token"
                    type="button"
                    class="vc-param-popover-item"
                    @mousedown.prevent
                    @click="insertParam(item.token)"
                  >
                    <span class="vc-param-popover-item__name">{{ item.label }}</span>
                    <span class="vc-param-popover-item__tag">{{ item.token }}</span>
                  </button>
                  <div v-if="!paramInsertOptions.length" class="vc-param-popover-empty">
                    请先在通话控制中配置客户信息参数
                  </div>
                </div>
              </div>

              <Button
                v-if="!openingAudioName"
                class="vc-opening-tool-btn"
                :loading="uploading"
                @click="triggerUpload"
              >
                <Icon type="ios-cloud-upload-outline" size="14" />
                上传音频
              </Button>
              <div v-if="openingAudioName" class="vc-audio-tag">
                <span class="vc-audio-tag__name" :title="openingAudioName">{{ openingAudioName }}</span>
                <button type="button" class="vc-audio-tag__del" title="删除录音" @click="clearOpeningAudio">
                  <Icon type="ios-trash-outline" size="14" />
                </button>
              </div>
            </div>
          </div>
          <div class="vc-opening-count">{{ openingTextLength }}/500</div>
        </div>
    </div>

    <input
      ref="audioFileInput"
      type="file"
      accept=".wav,audio/wav,audio/x-wav"
      class="vc-hidden-file"
      @change="handleAudioFileChange"
    />
  </div>
</template>

<script>
import { formatPauseTag, getAudioFileName } from '../../utils/callControlHelpers'
import { buildCustomerInfoInsertParamOptions } from '../../utils/flowSectionHelpers'
import { uploadAudioFile } from './audioUploadHelper'

export default {
  name: 'PresetOpeningSection',
  props: {
    callControl: {
      type: Object,
      required: true,
    },
    customerInfoParams: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      pausePopoverVisible: false,
      paramPopoverVisible: false,
      pauseCustomSec: 0,
      pausePresets: [0.5, 1, 2],
      uploading: false,
      openingHighlightHtml: '',
      openingEditorFocused: false,
      openingCaret: { start: 0, end: 0 },
    }
  },
  computed: {
    openingTextLength() {
      return String(this.callControl.preset_opening.text || '').length
    },
    openingAudioName() {
      return getAudioFileName(this.callControl.preset_opening.audio_file_path)
    },
    paramInsertOptions() {
      return buildCustomerInfoInsertParamOptions(this.customerInfoParams)
    },
  },
  mounted() {
    this.ensureShape()
    this.syncOpeningHighlight()
    this.onDocumentClick = () => {
      this.pausePopoverVisible = false
      this.paramPopoverVisible = false
    }
    document.addEventListener('click', this.onDocumentClick)
  },
  beforeDestroy() {
    if (this.onDocumentClick) {
      document.removeEventListener('click', this.onDocumentClick)
    }
  },
  watch: {
    'callControl.preset_opening.text': {
      handler() {
        this.syncOpeningHighlight()
      },
    },
  },
  methods: {
    ensureShape() {
      if (this.callControl.preset_opening_enabled == null) {
        this.$set(this.callControl, 'preset_opening_enabled', false)
      }
      if (!this.callControl.preset_opening || typeof this.callControl.preset_opening !== 'object') {
        this.$set(this.callControl, 'preset_opening', { text: '', audio_file_path: '' })
      }
    },
    escapeOpeningHtml(text) {
      return String(text || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
    },
    renderOpeningHighlight(text) {
      let html = this.escapeOpeningHtml(text)
      html = html.replace(/【停顿[\d.]+秒】/g, (match) => {
        return `<span class="vc-opening-tag-pause">${match}</span>`
      })
      html = html.replace(/#([\u4e00-\u9fa5\w]+)/g, '<span class="vc-opening-tag-param">#$1</span>')
      if (String(text).endsWith('\n')) html += ' '
      return html || '<br>'
    },
    syncOpeningHighlight() {
      this.openingHighlightHtml = this.renderOpeningHighlight(this.callControl.preset_opening.text)
      this.$nextTick(this.syncOpeningHighlightScroll)
    },
    syncOpeningHighlightScroll() {
      const textarea = this.$refs.openingTextarea
      const highlight = this.$refs.openingHighlight
      if (!textarea || !highlight) return
      highlight.scrollTop = textarea.scrollTop
      highlight.scrollLeft = textarea.scrollLeft
    },
    onOpeningTextareaFocus() {
      this.openingEditorFocused = true
      this.saveOpeningCaret()
    },
    onOpeningTextareaBlur() {
      this.saveOpeningCaret()
      this.openingEditorFocused = false
    },
    saveOpeningCaret() {
      const textarea = this.$refs.openingTextarea
      if (!textarea) return
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      this.openingCaret = {
        start: Number.isFinite(start) ? start : 0,
        end: Number.isFinite(end) ? end : 0,
      }
    },
    insertAtCursor(text) {
      const textarea = this.$refs.openingTextarea
      if (!textarea) return
      const start = this.openingCaret.start
      const end = this.openingCaret.end
      const val = String(this.callControl.preset_opening.text || '')
      const next = val.slice(0, start) + text + val.slice(end)
      if (next.length > 500) {
        this.$Message.warning('预设开场白最多 500 字')
        return
      }
      this.$set(this.callControl.preset_opening, 'text', next)
      const pos = start + text.length
      this.openingCaret = { start: pos, end: pos }
      this.$nextTick(() => {
        textarea.focus()
        textarea.setSelectionRange(pos, pos)
        this.syncOpeningHighlight()
      })
    },
    togglePausePopover() {
      const next = !this.pausePopoverVisible
      this.pausePopoverVisible = next
      if (next) this.paramPopoverVisible = false
    },
    toggleParamPopover() {
      const next = !this.paramPopoverVisible
      this.paramPopoverVisible = next
      if (next) this.pausePopoverVisible = false
    },
    insertPause(seconds) {
      const tag = formatPauseTag(seconds)
      if (!tag) return
      this.insertAtCursor(tag)
      this.pausePopoverVisible = false
    },
    confirmCustomPause() {
      const tag = formatPauseTag(this.pauseCustomSec)
      if (!tag) {
        this.$Message.warning('请输入大于 0 的停顿时长')
        return
      }
      this.insertAtCursor(tag)
      this.pausePopoverVisible = false
    },
    insertParam(tag) {
      if (!tag) return
      this.insertAtCursor(tag)
      this.paramPopoverVisible = false
    },
    triggerUpload() {
      const input = this.$refs.audioFileInput
      if (!input) return
      input.value = ''
      input.click()
    },
    async handleAudioFileChange(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      this.uploading = true
      try {
        const { filePath } = await uploadAudioFile(file, { message: this.$Message })
        this.$set(this.callControl.preset_opening, 'audio_file_path', String(filePath || '').trim())
        this.$Message.success('上传成功')
      } catch (e) {
        this.$Message.error((e && e.message) || '上传失败')
      } finally {
        this.uploading = false
      }
    },
    clearOpeningAudio() {
      this.$set(this.callControl.preset_opening, 'audio_file_path', '')
    },
  },
}
</script>

<style lang="scss" scoped>
@import './shared.scss';

.vc-section {
  margin-bottom: 16px;
}

.vc-card {
  background: #fff;
  border: 1px solid #e8eaec;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  padding: 20px;
}

.vc-card--compact {
  padding-bottom: 16px;
}

.vc-hidden-file {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}

.vc-preset-opening--collapsed {
  padding-bottom: 20px;

  .vc-preset-opening__head {
    margin-bottom: 0;
  }
}

.vc-preset-opening__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.vc-preset-opening__title-wrap {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
}

.vc-preset-opening__title {
  font-size: 15px;
  font-weight: 600;
  color: #262626;
  line-height: 24px;
}

.vc-preset-opening__hint {
  font-size: 12px;
  color: #8c8c8c;
  line-height: 24px;
}

.vc-preset-opening__switch {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.vc-preset-opening__switch-label {
  font-size: 13px;
  color: #595959;
  line-height: 24px;
}

.vc-preset-opening__body {
  padding-top: 0;
}

.vc-opening-wrap {
  position: relative;
}

.vc-opening-editor {
  position: relative;
  border: 1px solid #dcdee2;
  border-radius: 4px;
  background: #fff;
  min-height: 120px;
  transition: border-color 0.2s ease;

  &.is-focused {
    border-color: #2d8cf0;
  }
}

.vc-opening-highlight,
.vc-opening-textarea {
  box-sizing: border-box;
  width: 100%;
  min-height: 120px;
  padding: 10px 12px 44px;
  font-size: 13px;
  line-height: 1.55;
  border: none;
  outline: none;
  resize: vertical;
  font-family: inherit;
  white-space: pre-wrap;
  word-wrap: break-word;
  text-align: left;
  direction: ltr;
}

.vc-opening-highlight {
  position: absolute;
  inset: 0;
  z-index: 1;
  color: #262626;
  pointer-events: none;
  overflow: hidden;

  ::v-deep .vc-opening-tag-pause,
  ::v-deep .vc-opening-tag-param {
    color: #2d8cf0;
    display: inline;
  }
}

.vc-opening-textarea {
  position: relative;
  z-index: 2;
  background: transparent;
  color: transparent;
  caret-color: #262626;

  &::placeholder {
    color: #c5c8ce;
  }
}

.vc-opening-toolbar {
  position: absolute;
  left: 10px;
  bottom: 8px;
  z-index: 3;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  pointer-events: auto;
}

.vc-opening-dropdown {
  position: relative;

  &.is-open .vc-opening-popover {
    display: block;
  }

  &.is-open .vc-opening-tool-btn {
    border-color: #2d8cf0 !important;
    background: #ecf5ff !important;
  }
}

.vc-opening-tool-btn {
  height: 28px;
  padding: 0 10px !important;
  border: 1px solid #dcdee2 !important;
  border-radius: 4px !important;
  background: #fff !important;
  color: #2d8cf0 !important;
  font-size: 12px !important;
  box-shadow: none !important;

  &:hover,
  &:focus {
    border-color: #2d8cf0 !important;
    color: #1677ff !important;
    background: #f0f9ff !important;
  }

  &--compact {
    height: 24px;
    padding: 0 8px !important;
    font-size: 11px !important;
  }
}

.vc-opening-popover {
  display: none;
  position: absolute;
  left: 0;
  bottom: calc(100% + 6px);
  z-index: 20;
  min-width: 160px;
  padding: 8px 0;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);

  &--param {
    min-width: 200px;
    max-height: 220px;
    overflow-y: auto;
    padding: 8px 0;
  }
}

.vc-pause-preset {
  display: block;
  width: 100%;
  margin: 0;
  padding: 8px 14px;
  border: none;
  border-radius: 0;
  background: transparent;
  color: #262626;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    background: #f5f7fa;
    color: #2d8cf0;
  }
}

.vc-pause-custom {
  display: flex;
  align-items: center;
  margin: 4px 10px 8px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
}

.vc-pause-custom__input {
  flex: 1;
  min-width: 0;

  ::v-deep .ivu-input-number-input {
    border: none !important;
    box-shadow: none !important;
    height: 32px;
  }

  ::v-deep .ivu-input-number-handler-wrap {
    display: none;
  }
}

.vc-pause-custom__unit {
  padding: 0 10px;
  font-size: 13px;
  color: #8c8c8c;
  background: #fafafa;
  height: 32px;
  line-height: 32px;
  border-left: 1px solid #dcdfe6;
  flex-shrink: 0;
}

.vc-pause-confirm {
  display: block;
  width: calc(100% - 20px);
  margin: 0 10px !important;
}

.vc-param-popover-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 10px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: #f0f9ff;
  }

  &__name {
    font-size: 13px;
    color: #262626;
  }

  &__tag {
    font-size: 12px;
    color: #2d8cf0;
  }
}

.vc-param-popover-empty {
  padding: 12px;
  font-size: 12px;
  color: #8c8c8c;
  text-align: center;
}

.vc-opening-count {
  margin-top: 4px;
  text-align: right;
  font-size: 12px;
  color: #8c8c8c;
}

</style>
